import { Container as ContainerIcon } from 'lucide-react';
import { useAppStore } from '@/store/store';
import { selectContainerFits, selectShipmentSummary, selectPalletLoad } from '@/store/selectors';
import { calcPalletsPerContainer } from '@freight-cbm/shared-logic';
import { CONTAINER_SPECS } from '@freight-cbm/shared-logic';
import { PALLET_SPECS } from '@freight-cbm/shared-logic';
import { EmptyState } from '@/components/shared/EmptyState';

function UtilizationBar({ label, value }: { label: string; value: number }) {
  const color =
    value >= 90 ? 'var(--color-success)' : value >= 70 ? 'var(--color-warning)' : 'var(--color-accent)';
  return (
    <div style={{ marginBottom: 'var(--space-2)' }}>
      <div className="metric-label" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--space-1)' }}>
        <span>{label}</span>
        <span>{value.toFixed(1)}%</span>
      </div>
      <div className="progress-track">
        <div
          className="progress-fill"
          style={{ width: `${Math.min(value, 100)}%`, background: color }}
        />
      </div>
    </div>
  );
}

export function ContainerPanel() {
  const containerFits = useAppStore(selectContainerFits);
  const summary = useAppStore(selectShipmentSummary);
  const palletLoad = useAppStore(selectPalletLoad);
  const isEmpty = summary.totalCBM === 0 && summary.grossWeightKg === 0;

  const firstPallet = PALLET_SPECS[0];
  const firstLoad = palletLoad[0];

  return (
    <div className="card" data-testid="container-panel">
      <h3 className="section-title">Container Utilization</h3>
      {isEmpty ? (
        <EmptyState
          icon={ContainerIcon}
          compact
          title="Container fit analysis appears here"
          description="Add packages on the Calculator tab to see how they fill 20ft, 40ft, and 40ft HC containers by volume and weight."
        />
      ) : (
        <div className="metric-grid">
          {containerFits.map((fit, idx) => {
            const spec = CONTAINER_SPECS[idx];
            const palletizedContainers =
              firstPallet && firstLoad && spec && firstLoad.palletCount > 0
                ? Math.ceil(
                    firstLoad.palletCount /
                      calcPalletsPerContainer(spec, firstPallet).totalPalletsPerContainer,
                  )
                : 0;
            return (
              <div key={fit.containerType} className="metric-box">
                <div style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-2)' }}>
                  {fit.containerType}
                </div>
                <UtilizationBar label="Volume fill" value={fit.volumeUtilization} />
                <UtilizationBar label="Weight fill" value={fit.weightUtilization} />
                <div
                  className="metric-label"
                  style={{
                    display: 'inline-block',
                    padding: '2px var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Limited by: {fit.weightUtilization > fit.volumeUtilization ? 'weight' : 'volume'}
                </div>
                <div className="text-sm text-secondary">
                  <div>Containers needed: <strong>{fit.containerCount}</strong></div>
                  <div>Available CBM: {fit.availableCBM.toFixed(2)}</div>
                  {palletizedContainers > 0 && firstPallet && (
                    <div className="text-muted" style={{ marginTop: 'var(--space-1)' }}>
                      If palletized ({firstPallet.type.split(' ')[0]}): ~{palletizedContainers} containers (vs {fit.containerCount} loose-CBM)
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
